
import React from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, AlertTriangle, Info, Loader2 } from "lucide-react";

interface AnalysisStatusProps {
  error: string | null;
  success: boolean;
  isAnalyzing?: boolean;
  hasWebsite?: boolean;
}

const AnalysisStatus: React.FC<AnalysisStatusProps> = ({ 
  error, 
  success, 
  isAnalyzing = false,
  hasWebsite = false
}) => {
  // Show loading state
  if (isAnalyzing) {
    return (
      <Alert className="bg-blue-50 border-blue-200 mt-4">
        <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
        <AlertDescription className="text-blue-700">
          Analyzing your website... This may take a moment.
        </AlertDescription>
      </Alert>
    );
  }
  
  // When no website is entered yet
  if (!hasWebsite && !error && !success) {
    return (
      <Alert className="bg-amber-50 border-amber-200 mt-4">
        <Info className="h-4 w-4 text-amber-500" />
        <AlertDescription className="text-amber-700">
          Enter your website URL and click Analyze to extract business information automatically.
        </AlertDescription>
      </Alert>
    );
  }
  
  if (success) {
    return (
      <Alert className="bg-green-50 border-green-200 mt-4">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <AlertDescription className="text-green-700">
          Website analysis complete! Review the data before applying it to your profile.
        </AlertDescription>
      </Alert>
    );
  }
  
  if (error) {
    // Determine if this is a connectivity error, parser error, or content analysis error
    let variant = "destructive";
    let icon = <AlertTriangle className="h-4 w-4" />;
    let formattedError = error;
    
    if (error.includes("Failed to fetch") || error.includes("NetworkError") || error.includes("CORS")) {
      formattedError = "We couldn't connect to your website. Please check that the URL is correct and the site is accessible.";
    } else if (error.includes("no content returned") || error.includes("content too short")) {
      formattedError = "We couldn't extract enough content from your website. This could be due to content restrictions or a website built with technologies that make content extraction difficult.";
    } else if (error.includes("Failed to analyze website content")) {
      formattedError = "We couldn't analyze your website content. This could be due to restricted access, a temporary service issue, or insufficient content on the site. Please try again later or enter your business information manually.";
    } else if (error.includes("Edge function") || error.includes("Supabase function error")) {
      variant = "default";
      icon = <Info className="h-4 w-4 text-blue-500" />;
      formattedError = "Our analysis service is temporarily unavailable. Please try again later or enter your business information manually.";
    }
      
    return (
      <Alert variant={variant as any} className="mt-4">
        {icon}
        <AlertDescription>{formattedError}</AlertDescription>
      </Alert>
    );
  }
  
  return null;
};

export default AnalysisStatus;
